import { useState, useMemo, useEffect } from 'react';
import { Bell, Plus, ArrowRight, ChevronDown, ChevronUp, Settings, Trash2, Pin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NewNewsDialog } from './NewNewsDialog';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/Card';
import { cn } from '../../utils/cn';
import type { NewsItem, NewsSectionProps } from '../../types/news';
import { ActivityDialog } from '../ui/ActivityDialog';
import { KebabMenu } from '../ui/KebabMenu';
import { setCachedResidenceData, clearResidenceCache } from '../../utils/residenceCache';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('no', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }).replace(/\./g, '.');
};

export function NewsSection({ onPinChange, className }: NewsSectionProps) {
  const [showNewNewsDialog, setShowNewNewsDialog] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { currentResidence, residences, id: userId } = useUser();
  const { user } = useAuth();
  const [openNewsId, setOpenNewsId] = useState<number | null>(null);
  const [pinnedItemId, setPinnedItemId] = useState<number | null>(null);
  // Meny state håndteres av KebabMenu
  const queryClient = useQueryClient();

  const residenceId = currentResidence?.residence_id;

  // stripped render logging

  const isBoard = useMemo(() => {
    if (!residences || residences.length === 0) {
      return false;
    }
    // Sjekk om brukeren har board-rolle i noen av sine tilhørigheter
    return residences.some(r => r.role === 'board');
  }, [residences]);

  // React Query: hent nyheter per residence
  const { data: newsItems = [] } = useQuery({
    queryKey: ['residence', residenceId, 'news'],
    enabled: Boolean(residenceId),
    queryFn: async (): Promise<NewsItem[]> => {
      if (!residenceId) return [];

      const { data, error } = await supabase
        .from('residence_news')
        .select('*')
        .eq('residence_id', residenceId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching news items:', error);
        return [];
      }

      const formattedData: NewsItem[] = (data || []).map((item: any) => ({
        ...item,
        date: formatDate(item.created_at || ''),
      }));

      setCachedResidenceData(residenceId, { news: formattedData });
      return formattedData;
    }
  });

  const handlePinClick = async (item: NewsItem) => {
    if (!currentResidence?.id) return;

    try {
      // Optimistic update: oppdater UI umiddelbart
      const previousNews = queryClient.getQueryData<NewsItem[]>(['residence', residenceId, 'news']);
      
      if (previousNews) {
        // Oppdater alle items: sett is_pinned til false for alle, true for valgt item
        const updatedNews = previousNews.map(newsItem => ({
          ...newsItem,
          is_pinned: newsItem.id === item.id ? !item.is_pinned : false
        }));
        
        // Oppdater cache umiddelbart
        queryClient.setQueryData(['residence', residenceId, 'news'], updatedNews);
        if (residenceId) {
          setCachedResidenceData(residenceId, { news: updatedNews });
        }
      }

      // Hvis item allerede er pinned, unpin den
      if (item.is_pinned) {
        const { data, error } = await supabase
          .from('residence_news')
          .update({ is_pinned: false })
          .eq('id', item.id)
          .select()
          .single();

        if (error) {
          console.error('Error unpinning item:', error);
          // Revert optimistic update ved feil
          if (previousNews) {
            queryClient.setQueryData(['residence', residenceId, 'news'], previousNews);
          }
          return;
        }

        if (data) {
          onPinChange(null);
          clearResidenceCache(residenceId);
          void queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'news'] });
          void queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'pinned'] });
          void queryClient.refetchQueries({ queryKey: ['residence', residenceId, 'news'] });
          void queryClient.refetchQueries({ queryKey: ['residence', residenceId, 'pinned'] });
        }
        return;
      }

      // Fjern pin fra alle nyheter i samme bolig
      await supabase
        .from('residence_news')
        .update({ is_pinned: false })
        .eq('is_pinned', true)
        .eq('residence_id', currentResidence.residence_id);

      const { data, error } = await supabase
        .from('residence_news')
        .update({ is_pinned: true })
        .eq('id', item.id)
        .select()
        .single();

      if (error) {
        console.error('Error pinning item:', error);
        // Revert optimistic update ved feil
        if (previousNews) {
          queryClient.setQueryData(['residence', residenceId, 'news'], previousNews);
        }
      } else if (data) {
        setPinnedItemId(data.id);
        onPinChange(data);
        clearResidenceCache(residenceId);
        void queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'news'] });
        void queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'pinned'] });
        void queryClient.refetchQueries({ queryKey: ['residence', residenceId, 'news'] });
        void queryClient.refetchQueries({ queryKey: ['residence', residenceId, 'pinned'] });
      }
    } catch (error) {
      console.error('Error handling pin click:', error);
      // Revert optimistic update ved feil
      const previousNews = queryClient.getQueryData<NewsItem[]>(['residence', residenceId, 'news']);
      if (previousNews) {
        queryClient.setQueryData(['residence', residenceId, 'news'], previousNews);
      }
    }
  };

  const handleDeleteClick = async (item: NewsItem) => {
    try {
      // Enkel bekreftelse for nå
       
      const confirmed = window.confirm('Er du sikker på at du vil slette denne nyheten?');
      if (!confirmed) return;

      const { error } = await supabase
        .from('residence_news')
        .delete()
        .eq('id', item.id);
      if (error) {
        console.error('Error deleting news item:', error);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['residence', currentResidence?.residence_id, 'news'] });
      void queryClient.invalidateQueries({ queryKey: ['residence', currentResidence?.residence_id, 'pinned'] });
    } catch (e) {
      console.error('Unexpected delete error:', e);
    }
  };

  // Realtime subscription fjernet - kan aktiveres senere hvis Realtime er aktivert for residence_news tabellen

  const visibleNews = useMemo(() => 
    isExpanded ? newsItems : newsItems.slice(0, 5),
    [isExpanded, newsItems]
  );

  // Reset animation state after animation completes
  useEffect(() => {
    if (pinnedItemId) {
      const timer = setTimeout(() => {
        setPinnedItemId(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pinnedItemId]);

  return (
    <Card className={className} aria-labelledby="news-heading">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle icon={Bell} size="lg" id="news-heading">
            Siste nytt fra styret
          </CardTitle>
          {isBoard && (
            <Button 
              onClick={() => setShowNewNewsDialog(true)}
              className={cn(
                'flex items-center gap-1 px-2 sm:px-3 py-2 sm:py-2',
                'bg-condo-dark rounded-lg hover:bg-condo-med transition-colors text-condo-light',
                'focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2'
              )}
              variant="condo"
              aria-label="Legg til nyhet"
            >
              <Plus className="w-4 h-4 font-extrabold sm:font-medium" aria-hidden="true" />
              <span className="hidden sm:flex text-sm">Legg til nyhet</span>
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {newsItems.length === 0 ? (
          <div className="text-sm sm:text-base text-gray-600 text-center py-6">
            Ingen nyheter fra styret for øyeblikket.
          </div>
        ) : (
          <div 
            className="divide-y divide-gray-100"
            role="feed"
            aria-label="Liste over nyheter"
          >
            {visibleNews.map((item) => {
            const isPinned = item.is_pinned === true;
            const shouldAnimate = pinnedItemId === item.id;
            
            return (
              <article 
                key={item.id} 
                className={cn(
                  "relative hover:bg-gray-50 transition-colors cursor-pointer",
                  shouldAnimate && "animate-pinned-news"
                )}
                role="article"
                onClick={() => setOpenNewsId(item.id as unknown as number)}
              >
                <div className="p-0 sm:p-4 pl-0 sm:pl-4">
                  <div className="sm:flex sm:items-start sm:justify-between sm:gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mt-3 gap-2">
                        <h3 className="font-semibold mb-1 text-sm sm:text-base flex-1 pr-2">{item.title}</h3>
                        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          {isBoard && (
                            <button
                              type="button"
                              onClick={() => handlePinClick(item)}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                              aria-label={isPinned ? "Avfeste melding" : "Feste melding som viktig"}
                            >
                              <Pin 
                                className={cn(
                                  "w-4 h-4 transition-colors",
                                  isPinned ? "text-condo-dark" : "text-gray-400"
                                )}
                                fill={isPinned ? "currentColor" : "none"}
                              />
                            </button>
                          )}
                          {!isBoard && (
                            <Pin 
                              className={cn(
                                "w-4 h-4 transition-colors",
                                isPinned ? "text-condo-dark" : "text-gray-400"
                              )}
                              fill={isPinned ? "currentColor" : "none"}
                              aria-label={isPinned ? "Festet som viktig melding" : "Ikke festet"}
                            />
                          )}
                          {isBoard && (
                            <KebabMenu
                              items={[
                                { label: 'Rediger post', icon: <Settings className="w-4 h-4" />, onSelect: () => setOpenNewsId(item.id as unknown as number) },
                                { label: 'Slett innlegg', icon: <Trash2 className="w-4 h-4" />, onSelect: () => handleDeleteClick(item) },
                              ]}
                              icon="horizontal"
                              align="end"
                              size="sm"
                            />
                          )}
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm sm:text-base whitespace-pre-line">{item.content}</p>
                      <time 
                        className="text-xs sm:text-sm text-gray-500 mt-1 block"
                        dateTime={item.created_at || ''}
                      >
                        Publisert: {item.date}
                      </time>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
          </div>
        )}

        {newsItems.length > 4 && (
          <div className="sm:hidden flex justify-end">
            <Link 
              to="/" 
              className={cn(
                "flex items-center gap-1 text-xs text-condo-dark hover:text-condo-med transition-colors mt-2",
                "focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2 rounded"
              )}
            >
              <span>Les flere poster fra styret</span>
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          </div>
        )}

        {newsItems.length > 4 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              "w-full p-3 pb-0 text-sm text-gray-600 hover:text-gray-900 hidden sm:flex items-center justify-center gap-2 border-t mt-2",
              "focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2"
            )}
            aria-expanded={isExpanded}
            aria-controls="news-feed"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
                <span>Vis mindre</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                <span>Vis mer</span>
              </>
            )}
          </button>
        )}
      </CardContent>

      {showNewNewsDialog && (
        <NewNewsDialog
          onClose={() => setShowNewNewsDialog(false)}
          onSubmit={async (formData) => {
            try {
              if (!currentResidence?.residence_id) {
                console.error('Ingen currentResidence.residence_id funnet');
                return;
              }

              const { data, error } = await supabase
                .from('residence_news')
                .insert({
                  title: formData.title,
                  content: formData.content,
                  residence_id: currentResidence.residence_id,
                  is_pinned: false,
                  user_id: userId
                })
                .select()
                .single();

              if (error) {
                console.error('Error creating news item:', error);
              } else if (data) {
                // Log activity til board_activity_log
                try {
                  await supabase
                    .from('board_activity_log')
                    .insert({
                      residence_id: currentResidence.residence_id,
                      user_id: user?.id,
                      activity_type: 'news',
                      entity_id: data.id,
                      entity_table: 'residence_news'
                    });
                } catch (logError) {
                  console.error('Kunne ikke logge aktivitet:', logError);
                }

                // Invalider for å oppdatere liste
                void queryClient.invalidateQueries({ queryKey: ['residence', residenceId, 'news'] });
                setShowNewNewsDialog(false);
              }
            } catch (error) {
              console.error('Error in onSubmit:', error);
            }
          }}
        />
      )}

      {/* Activity dialog for nyheter */}
      <ActivityDialog
        activityId={openNewsId}
        activityType={'news'}
        open={openNewsId !== null}
        onOpenChange={(open) => {
          if (!open) setOpenNewsId(null);
        }}
        showComments={true}
        showLikes={true}
        showAttendance={false}
        showEditButton={true}
      />
    </Card>
  );
}